/**
 * Dialer Dashboard
 * 
 * Çoklu kampanya real-time izleme dashboard'u
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    CardHeader,
    IconButton,
    Chip,
    LinearProgress,
    Button,
    Alert,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    CircularProgress
} from '@mui/material';
import {
    PlayArrow as PlayIcon,
    Pause as PauseIcon,
    Stop as StopIcon,
    Refresh as RefreshIcon,
    Phone as PhoneIcon,
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon,
    Speed as SpeedIcon,
    CallMissed as MissedIcon,
    CallReceived as AnsweredIcon,
    Timer as TimerIcon,
    PhoneCallback as CallbackIcon,
    PowerSettingsNew as PowerIcon
} from '@mui/icons-material';
import campaignService, { CampaignStatus } from '../services/campaignService';
import io from 'socket.io-client';
import { SOCKET_URL } from '../config';

interface ServiceStatus {
    isRunning: boolean;
    activeCampaigns: number;
    activeDialerCalls: number;
    amiConnected: boolean;
}

interface DashboardData {
    serviceStatus: ServiceStatus;
    campaigns: CampaignStatus[];
}

export default function DialerDashboard() {
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [socket, setSocket] = useState<any>(null);
    
    // Socket connection
    useEffect(() => {
        const newSocket = io(SOCKET_URL);
        setSocket(newSocket);
        
        return () => {
            newSocket.disconnect();
        };
    }, []);
    
    // Load dashboard data
    const loadData = useCallback(async () => {
        try {
            const result = await campaignService.getActiveCampaignsStatus();
            setData(result);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Veriler yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    }, []);
    
    useEffect(() => {
        loadData();
    }, [loadData]);
    
    // Auto refresh
    useEffect(() => {
        if (!autoRefresh) return;
        
        const interval = setInterval(loadData, 5000); // Every 5 seconds
        return () => clearInterval(interval);
    }, [autoRefresh, loadData]);
    
    // Socket events
    useEffect(() => {
        if (!socket) return;
        
        const handleDialerEvent = (event: any) => {
            console.log('Dialer event:', event);
            // Refresh data on important events
            if (['campaign-started', 'campaign-stopped', 'campaign-completed'].includes(event.event)) {
                loadData();
            }
        };
        
        socket.on('dialer-event', handleDialerEvent);
        
        return () => {
            socket.off('dialer-event', handleDialerEvent);
        };
    }, [socket, loadData]);
    
    // Service control
    const handleStartService = async () => {
        try {
            await campaignService.startDialerService();
            loadData();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    const handleStopService = async () => {
        try {
            await campaignService.stopDialerService();
            loadData();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    // Campaign control
    const handlePause = async (campaignId: string) => {
        try {
            await campaignService.pauseCampaign(campaignId);
            loadData();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    const handleResume = async (campaignId: string) => {
        try {
            await campaignService.resumeCampaign(campaignId);
            loadData();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    const handleStop = async (campaignId: string) => {
        try {
            await campaignService.stopCampaign(campaignId);
            loadData();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    // Calculate totals
    const getTotals = () => {
        if (!data?.campaigns) return { calls: 0, agents: 0, answered: 0, contacts: 0 };
        
        return data.campaigns.reduce((acc, c) => ({
            calls: acc.calls + (c.realtime?.activeCallCount || 0),
            agents: acc.agents + (c.realtime?.availableAgentCount || 0),
            answered: acc.answered + (c.realtime?.dialCount || 0),
            contacts: acc.contacts + Object.values(c.contacts).reduce((s, v) => s + v, 0)
        }), { calls: 0, agents: 0, answered: 0, contacts: 0 });
    };
    
    const totals = getTotals();
    
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }
    
    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    <PhoneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Dialer Dashboard
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                        label={autoRefresh ? 'Otomatik Yenileme: Açık' : 'Otomatik Yenileme: Kapalı'}
                        color={autoRefresh ? 'success' : 'default'}
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        size="small"
                    />
                    <IconButton onClick={loadData}>
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>
            
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            
            {/* Service Status */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PowerIcon color={data?.serviceStatus.isRunning ? 'success' : 'disabled'} />
                        <Box>
                            <Typography variant="h6">
                                Dialer Servisi
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {data?.serviceStatus.isRunning ? 'Çalışıyor' : 'Durduruldu'}
                                {' • '}
                                AMI: {data?.serviceStatus.amiConnected ? 'Bağlı' : 'Bağlı Değil'}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {data?.serviceStatus.isRunning ? (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<StopIcon />}
                                onClick={handleStopService}
                            >
                                Servisi Durdur
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<PlayIcon />}
                                onClick={handleStartService}
                            >
                                Servisi Başlat
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>
            
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 6, sm: 3 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="textSecondary" variant="body2">
                                        Aktif Çağrılar
                                    </Typography>
                                    <Typography variant="h3">
                                        {totals.calls}
                                    </Typography>
                                </Box>
                                <PhoneIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="textSecondary" variant="body2">
                                        Müsait Agentlar
                                    </Typography>
                                    <Typography variant="h3">
                                        {totals.agents}
                                    </Typography>
                                </Box>
                                <PeopleIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="textSecondary" variant="body2">
                                        Aktif Kampanyalar
                                    </Typography>
                                    <Typography variant="h3">
                                        {data?.serviceStatus.activeCampaigns || 0}
                                    </Typography>
                                </Box>
                                <TrendingUpIcon sx={{ fontSize: 48, color: 'info.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography color="textSecondary" variant="body2">
                                        Toplam Kişi
                                    </Typography>
                                    <Typography variant="h3">
                                        {totals.contacts}
                                    </Typography>
                                </Box>
                                <CallbackIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            
            {/* Campaign Cards */}
            {data?.campaigns && data.campaigns.length > 0 ? (
                <Grid container spacing={2}>
                    {data.campaigns.map((campaign) => (
                        <Grid size={{ xs: 12, md: 6 }} key={campaign.campaign.id}>
                            <Card>
                                <CardHeader
                                    title={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {campaign.campaign.name}
                                            <Chip
                                                label={campaign.campaign.status}
                                                color={campaign.campaign.status === 'running' ? 'success' : 'warning'}
                                                size="small"
                                            />
                                        </Box>
                                    }
                                    action={
                                        <Box>
                                            {campaign.campaign.status === 'running' && (
                                                <Tooltip title="Duraklat">
                                                    <IconButton onClick={() => handlePause(campaign.campaign.id)}>
                                                        <PauseIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {campaign.campaign.status === 'paused' && (
                                                <Tooltip title="Devam Et">
                                                    <IconButton color="success" onClick={() => handleResume(campaign.campaign.id)}>
                                                        <PlayIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Durdur">
                                                <IconButton color="error" onClick={() => handleStop(campaign.campaign.id)}>
                                                    <StopIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                                <CardContent>
                                    {campaign.realtime && (
                                        <>
                                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                                <Grid size={{ xs: 6 }}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="h4" color="primary">
                                                            {campaign.realtime.activeCallCount}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Aktif Çağrı
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="h4" color="success.main">
                                                            {campaign.realtime.availableAgentCount}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            Müsait Agent
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                            
                                            <Divider sx={{ my: 2 }} />
                                            
                                            <Grid container spacing={1}>
                                                <Grid size={{ xs: 4 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <SpeedIcon fontSize="small" color="action" />
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {campaign.realtime.currentDialRatio.toFixed(1)}x
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Dial Ratio
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid size={{ xs: 4 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <AnsweredIcon fontSize="small" color="success" />
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {Math.round(campaign.realtime.answerRate * 100)}%
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Cevaplama
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid size={{ xs: 4 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <MissedIcon fontSize="small" color="error" />
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {Math.round(campaign.realtime.abandonRate * 100)}%
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Abandon
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                            
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Toplam Arama: {campaign.realtime.dialCount}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                    
                                    {/* Contact Progress */}
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="caption" color="textSecondary" gutterBottom>
                                            Kişi Durumu
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                                            {campaign.contacts.pending !== undefined && (
                                                <Chip label={`Bekleyen: ${campaign.contacts.pending || 0}`} size="small" color="info" variant="outlined" />
                                            )}
                                            {campaign.contacts.completed !== undefined && (
                                                <Chip label={`Tamamlanan: ${campaign.contacts.completed || 0}`} size="small" color="success" variant="outlined" />
                                            )}
                                            {campaign.contacts.failed !== undefined && (
                                                <Chip label={`Başarısız: ${campaign.contacts.failed || 0}`} size="small" color="error" variant="outlined" />
                                            )}
                                            {campaign.contacts.dnc !== undefined && (
                                                <Chip label={`DNC: ${campaign.contacts.dnc || 0}`} size="small" color="warning" variant="outlined" />
                                            )}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <PhoneIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                        Aktif Kampanya Yok
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Kampanya başlatmak için Kampanya Yönetimi sayfasına gidin.
                    </Typography>
                    <Button
                        variant="contained"
                        href="/campaigns"
                    >
                        Kampanya Yönetimi
                    </Button>
                </Paper>
            )}
        </Box>
    );
}
